// Vanilla ranked search over the small /search-index.json file the header
// lazy-loads on first focus — no fuzzy-search dependency, just tiered
// substring matching that's cheap enough to re-run on every keystroke
// against 53 entries.

export interface SearchIndexEntry {
  id: string;
  title: string;
  path: string;
  /** Category name shown under the title in results (may be empty in an older cached index). */
  category?: string;
  keywords: string[];
}

const MAX_RESULTS = 8;

let indexPromise: Promise<SearchIndexEntry[]> | null = null;

// Cached at module scope (not per-component) so re-focusing the search
// after the first load never re-fetches — the index only changes on a new
// deploy, which reloads the page anyway.
export function loadSearchIndex(): Promise<SearchIndexEntry[]> {
  if (!indexPromise) {
    indexPromise = fetch('/search-index.json')
      .then((res) => {
        if (!res.ok) throw new Error(`search-index.json responded ${res.status}`);
        return res.json();
      })
      .catch((err) => {
        // Let the next focus try again instead of caching a permanent failure.
        indexPromise = null;
        throw err;
      });
  }
  return indexPromise;
}

type Tier = 0 | 1 | 2 | 3 | 4;

// Best (lowest/most-specific) tier this single token achieves against one
// entry, or null if the token matches nowhere on this entry at all.
function tierForToken(token: string, titleLower: string, titleWords: string[], keywordsLower: string[]): Tier | null {
  if (titleLower.startsWith(token)) return 1;
  if (titleWords.some((word) => word.startsWith(token))) return 2;
  if (titleLower.includes(token)) return 3;
  if (keywordsLower.some((keyword) => keyword.includes(token))) return 4;
  return null;
}

interface ScoredEntry {
  entry: SearchIndexEntry;
  worstTier: Tier;
  tierSum: number;
}

// Every token must match somewhere (title or keywords), per the spec — an
// entry where even one token matches nothing is excluded entirely rather
// than partially ranked. Tiers rank exact title match > title-prefix >
// word-prefix > substring > keyword match; a multi-word query's overall
// rank is only as good as its weakest-matching token (worstTier), since a
// query is no stronger than its least-specific word — tierSum only breaks
// ties between entries that share the same worstTier.
export function rankResults(query: string, index: SearchIndexEntry[]): SearchIndexEntry[] {
  const normalizedQuery = query.trim().toLowerCase();
  const tokens = normalizedQuery.split(/\s+/).filter(Boolean);
  if (tokens.length === 0) return [];

  const scored: ScoredEntry[] = [];

  for (const entry of index) {
    const titleLower = entry.title.toLowerCase();

    if (titleLower === normalizedQuery) {
      scored.push({ entry, worstTier: 0, tierSum: 0 });
      continue;
    }

    const titleWords = titleLower.split(/\s+/);
    const keywordsLower = entry.keywords.map((k) => k.toLowerCase());

    let worstTier: Tier = 1;
    let tierSum = 0;
    let allMatched = true;

    for (const token of tokens) {
      const tier = tierForToken(token, titleLower, titleWords, keywordsLower);
      if (tier === null) {
        allMatched = false;
        break;
      }
      tierSum += tier;
      if (tier > worstTier) worstTier = tier;
    }

    if (allMatched) scored.push({ entry, worstTier, tierSum });
  }

  // Nothing matched exactly: forgive typos (a swapped, missing or extra letter) so "jsno" still finds JSON tools.
  if (scored.length === 0) return fuzzyResults(tokens, index);

  scored.sort((a, b) => {
    if (a.worstTier !== b.worstTier) return a.worstTier - b.worstTier;
    if (a.tierSum !== b.tierSum) return a.tierSum - b.tierSum;
    return a.entry.title.localeCompare(b.entry.title);
  });

  return scored.slice(0, MAX_RESULTS).map((s) => s.entry);
}

/** Edit distance counting a swap of two neighbouring letters as one change (optimal string alignment). */
export function editDistance(a: string, b: string, limit = 3): number {
  if (Math.abs(a.length - b.length) > limit) return limit + 1;
  const rows: number[][] = [];
  for (let i = 0; i <= a.length; i++) {
    rows.push(new Array(b.length + 1).fill(0));
    rows[i][0] = i;
  }
  for (let j = 0; j <= b.length; j++) rows[0][j] = j;
  for (let i = 1; i <= a.length; i++) {
    for (let j = 1; j <= b.length; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      rows[i][j] = Math.min(rows[i - 1][j] + 1, rows[i][j - 1] + 1, rows[i - 1][j - 1] + cost);
      if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) rows[i][j] = Math.min(rows[i][j], rows[i - 2][j - 2] + 1);
    }
  }
  return rows[a.length][b.length];
}

/** How many typos a word of this length may contain: none for 1-3 letters, one for 4-6, two beyond. */
const allowedTypos = (length: number) => (length <= 3 ? 0 : length <= 6 ? 1 : 2);

function fuzzyResults(tokens: string[], index: SearchIndexEntry[]): SearchIndexEntry[] {
  const scored: { entry: SearchIndexEntry; total: number }[] = [];
  for (const entry of index) {
    const words = [entry.title, ...entry.keywords].flatMap((text) => text.toLowerCase().split(/[\s/&,.-]+/)).filter(Boolean);
    let total = 0;
    let all = true;
    for (const token of tokens) {
      const limit = allowedTypos(token.length);
      let best = Infinity;
      for (const word of words) {
        // Compare with the word itself and with its first letters, so a typo in a half-typed word still counts.
        const d = Math.min(editDistance(token, word, limit), word.length > token.length ? editDistance(token, word.slice(0, token.length), limit) : Infinity);
        if (d < best) best = d;
        if (best === 0) break;
      }
      if (best > limit) {
        all = false;
        break;
      }
      total += best;
    }
    if (all) scored.push({ entry, total });
  }
  scored.sort((a, b) => a.total - b.total || a.entry.title.localeCompare(b.entry.title));
  return scored.slice(0, MAX_RESULTS).map((s) => s.entry);
}
